import React, { useContext, forwardRef } from "react";
import { formatPrice, getSystemSettings } from "../../../../functions/Functions";
import { SettingsContext } from "../../../../contexts/Settings";

const StockItemPrint = forwardRef(({
  name = "",
  title = "",
  printDetailView,
  meta,
  columns = [],
  data = [],
  totals = {},
  showTotals = false,
  showSignatures = false,
  signatures = null,
  className = "",
  PrintView = null,
}, ref) => {
  const { generalSettings } = useContext(SettingsContext);

  const targetRef = ref || PrintView;
  const detailView = printDetailView || meta;
  const docTitle = name || title;

  const getValue = (column, row, index) => {
    if (typeof column.render === "function") {
      return column.render(row, index);
    }

    if (typeof column.accessor === "function") {
      return column.accessor(row, index);
    }

    return row?.[column.accessor];
  };

  const renderValue = (column, row, index) => {
    const value = getValue(column, row, index);

    if (column.format === "price") {
      return formatPrice(value);
    }

    return value ?? "";
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return "";
    if (dateVal instanceof Date) return dateVal.toDateString();
    return dateVal;
  };

  const renderSignatures = () => {
    if (signatures && Array.isArray(signatures)) {
      return (
        <div className="mt-5 row">
          <div className="mt-5 col-12"></div>
          <div className="mt-5 col-12"></div>
          <div className="mt-5 col-12"></div>
          {signatures.map((sig, idx) => (
            <div className="col-6 row" key={idx}>
              <div className="col-4 d-flex align-items-end">{sig}</div>
              <div className="col-8 border-bottom border-1 border-dark"></div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="mt-5 row">
        <div className="mt-5 col-12"></div>
        <div className="mt-5 col-12"></div>
        <div className="mt-5 col-12"></div>
        <div className="col-6 row">
          <div className="col-4 d-flex align-items-end">Approved By </div>
          <div className="col-8 border-bottom border-1 border-dark"></div>
        </div>
        <div className="col-6 row">
          <div className="col-4 d-flex align-items-end">Manager Procurement </div>
          <div className="col-8 border-bottom border-1 border-dark"></div>
        </div>
      </div>
    );
  };

  const content = (
    <div className="fk-print" style={{ margin: "20px" }}>
      {detailView && (
        <div className="row mb-3">
          <div className="col-12">
            {generalSettings && (
              <div className="mx-auto" style={{ width: "90px" }}>
                <img
                  src={`${getSystemSettings(generalSettings, "type_logo")}`}
                  style={{ width: "100%" }}
                  alt="logo"
                />
              </div>
            )}
            {generalSettings && (
              <div className="my-2 xlg-print-text-stock print-text-stock text-center text-capitalize print-text-stock--bold">
                {getSystemSettings(generalSettings, "siteName")}
              </div>
            )}
            {detailView.branch && (
              <div className="my-2 xlg-print-text-stock print-text-stock text-center text-capitalize print-text-stock--bold">
                {detailView.branch?.name || detailView.branch}
              </div>
            )}
            {docTitle && (
              <div className="my-2 xlg-print-text-stock print-text-stock text-center text-capitalize print-text-stock--bold">
                {docTitle}
              </div>
            )}

            <div className="mt-4 my-2 print-text-stock print-text-stock--bold row text-capitalize">
              {(detailView.doc_id || detailView.document_identity) && (
                <div className="col-6">
                  Document No.: {detailView.doc_id || detailView.document_identity}
                </div>
              )}
              {detailView.date && (
                <div className="col-6">
                  Doc Date : {formatDate(detailView.date)}
                </div>
              )}
            </div>

            {(detailView.supplier || detailView.ref_doc || detailView.ref_document_identity || detailView.delivery_date || detailView.remarks) && (
              <div className="print-text-stock text-capitalize print-text-stock--bold row">
                {detailView.delivery_date && (
                  <div className="my-2 col-6">
                    Delivery Date : {formatDate(detailView.delivery_date)}
                  </div>
                )}
                {detailView.supplier && (
                  <div className="my-2 col-6">
                    Supplier Name :{" "}
                    {detailView.supplier?.name || detailView.supplier}
                  </div>
                )}
                {(detailView.ref_doc || detailView.ref_document_identity) && (
                  <div className="my-2 col-6">
                    Document Ref :{" "}
                    {detailView.ref_doc || detailView.ref_document_identity}
                  </div>
                )}
                {detailView.remarks && (
                  <div className="my-2 col-6">
                    Description : {detailView.remarks}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {data !== null && data.length > 0 && (
        <div className="mt-3">
          <table className={`table table-bordered-sm table-hover ${className}`}>
            <thead className="align-middle">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={`text-capitalize align-middle text-center border-dark border-1 border ${column.headerClassName || ""}`}
                    style={column.headerStyle}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="align-middle">
              {data.map((row, index) => (
                <tr className="align-middle" key={row.id ?? index}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`text-capitalize border-dark border-1 align-middle text-center ${column.cellClassName || ""}`}
                      style={column.cellStyle}
                    >
                      {renderValue(column, row, index)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>

            {showTotals && (
              <tbody className="align-middle">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`text-capitalize align-middle text-center border-dark border-1 border ${column.totalClassName || ""}`}
                    >
                      {column.total
                        ? column.total(totals)
                        : ""}
                    </th>
                  ))}
                </tr>
              </tbody>
            )}
          </table>
        </div>
      )}

      {(showSignatures || signatures || docTitle === "Purchase Order") && renderSignatures()}
    </div>
  );

  if (targetRef) {
    return (
      <div className="d-none">
        <div ref={targetRef}>
          {content}
        </div>
      </div>
    );
  }

  return content;
});

export default StockItemPrint;